import React, { useEffect, useState, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft, CheckCircle2, Clock, Truck, Package, MapPin, Phone, MessageCircle, User, Car, AlertCircle } from 'lucide-react';
import axios from 'axios';

const API = window.location.origin + '/api';

const TIMELINE_STEPS = [
  { key: 'pending', label: 'Order Placed', icon: Clock },
  { key: 'confirmed', label: 'Confirmed', icon: CheckCircle2 },
  { key: 'preparing', label: 'Preparing', icon: Package },
  { key: 'out_for_delivery', label: 'Out for Delivery', icon: Truck },
  { key: 'delivered', label: 'Delivered', icon: CheckCircle2 },
];

const SLOT_LABELS = {
  morning: 'Morning (8AM-12PM)',
  afternoon: 'Afternoon (12PM-4PM)',
  evening: 'Evening (4PM-8PM)',
};

const formatTime = (iso) => {
  if (!iso) return '';
  try {
    return new Date(iso).toLocaleString('en-US', {
      month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit',
    });
  } catch { return iso; }
};

const sanitizePhone = (phone) => (phone || '').replace(/[^\d+]/g, '');

export default function OrderTracking() {
  const { orderId } = useParams();
  const navigate = useNavigate();
  const [tracking, setTracking] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const fetchTracking = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/orders/${orderId}/tracking`, { withCredentials: true });
      setTracking(data);
      setError('');
    } catch (err) {
      console.error('Failed to load tracking:', err);
      setError(err.response?.data?.detail || 'Could not load tracking info');
    }
    setLoading(false);
  }, [orderId]);

  useEffect(() => {
    fetchTracking();
    const interval = setInterval(fetchTracking, 30000); // refresh every 30s
    return () => clearInterval(interval);
  }, [fetchTracking]);

  if (loading) return <div className="loading-screen" data-testid="tracking-loading">Loading tracking info...</div>;

  if (error || !tracking) {
    return (
      <div className="orders-page" data-testid="tracking-error">
        <button className="back-btn" onClick={() => navigate(-1)}><ArrowLeft /> Back</button>
        <div className="empty-state">
          <AlertCircle size={64} />
          <h3>Order not found</h3>
          <p>{error || 'We could not load tracking info for this order.'}</p>
        </div>
      </div>
    );
  }

  const currentIndex = TIMELINE_STEPS.findIndex(s => s.key === tracking.status);
  const isCancelled = tracking.status === 'cancelled';

  // map status -> timestamp from history (first occurrence)
  const stepTimes = {};
  (tracking.status_history || []).forEach(entry => {
    if (!stepTimes[entry.status]) stepTimes[entry.status] = entry.timestamp;
  });

  const driverPhone = sanitizePhone(tracking.driver_phone);
  const waLink = driverPhone ? `https://wa.me/${driverPhone.replace(/^\+/, '')}` : '';

  return (
    <div className="orders-page" data-testid="order-tracking-page">
      <button className="back-btn" onClick={() => navigate(-1)} data-testid="back-btn">
        <ArrowLeft /> Back
      </button>

      <div style={{ marginBottom: '20px' }}>
        <h2 style={{ margin: 0, color: 'var(--navy)' }}>Track Order</h2>
        <div style={{ fontSize: '0.9rem', color: 'var(--text-muted)', marginTop: '4px' }} data-testid="tracking-order-id">
          #{tracking.id.slice(0, 8)}
        </div>
      </div>

      {/* Order summary card */}
      <div className="order-card" data-testid="tracking-summary">
        {tracking.supermarket_name && (
          <div style={{ fontSize: '0.85rem', color: 'var(--text-muted)', marginBottom: '6px' }}>
            From: <strong>{tracking.supermarket_name}</strong>
          </div>
        )}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '8px' }}>
          <span className="order-total">{Number(tracking.total).toFixed(2)} PGK</span>
          <span className={`order-status status-${tracking.status}`} data-testid="tracking-current-status">
            {tracking.status.replace(/_/g, ' ')}
          </span>
        </div>
        <div style={{ fontSize: '0.82rem', color: 'var(--text-muted)', marginTop: '8px' }}>
          {tracking.items_count} item{tracking.items_count !== 1 ? 's' : ''} • Placed {formatTime(tracking.created_at)}
        </div>
        {(tracking.delivery_date || tracking.delivery_time_slot) && (
          <div style={{ fontSize: '0.82rem', marginTop: '8px', padding: '6px 10px', background: '#EFF6FF', borderRadius: '6px', display: 'flex', alignItems: 'center', gap: '6px', color: '#1E40AF' }}>
            <Clock size={13} /> {tracking.delivery_date || 'N/A'} &mdash; {SLOT_LABELS[tracking.delivery_time_slot] || tracking.delivery_time_slot || 'N/A'}
          </div>
        )}
        {tracking.delivery_address && (
          <div style={{ fontSize: '0.82rem', marginTop: '6px', color: 'var(--text-muted)', display: 'flex', alignItems: 'center', gap: '4px' }}>
            <MapPin size={13} /> {tracking.delivery_address}
          </div>
        )}
      </div>

      {/* Cancelled banner */}
      {isCancelled && (
        <div data-testid="tracking-cancelled" style={{ marginTop: '16px', padding: '14px 16px', background: '#FEF2F2', border: '1px solid #FCA5A5', color: '#991B1B', borderRadius: '8px', display: 'flex', alignItems: 'center', gap: '8px' }}>
          <AlertCircle size={18} /> This order has been cancelled.
        </div>
      )}

      {/* Driver card */}
      {tracking.driver_name && !isCancelled && (
        <div data-testid="tracking-driver-card" style={{ marginTop: '16px', padding: '16px', background: 'var(--navy)', color: 'white', borderRadius: '12px' }}>
          <div style={{ fontSize: '0.78rem', textTransform: 'uppercase', letterSpacing: '0.5px', opacity: 0.7, marginBottom: '8px' }}>Your Driver</div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '6px' }}>
            <User size={18} /> <strong data-testid="tracking-driver-name">{tracking.driver_name}</strong>
          </div>
          {tracking.vehicle_info && (
            <div style={{ display: 'flex', alignItems: 'center', gap: '10px', fontSize: '0.9rem', opacity: 0.9, marginBottom: '6px' }}>
              <Car size={16} /> {tracking.vehicle_info}
            </div>
          )}
          {tracking.estimated_delivery && (
            <div style={{ display: 'flex', alignItems: 'center', gap: '10px', fontSize: '0.9rem', opacity: 0.9, marginBottom: '12px' }}>
              <Clock size={16} /> ETA: {tracking.estimated_delivery}
            </div>
          )}
          {tracking.driver_phone && (
            <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', marginTop: '10px' }}>
              <a href={`tel:${driverPhone}`} data-testid="driver-call-btn"
                style={{ display: 'inline-flex', alignItems: 'center', gap: '6px', padding: '8px 14px', borderRadius: '8px', background: 'var(--lime)', color: 'var(--navy)', fontSize: '0.85rem', fontWeight: 600, textDecoration: 'none' }}>
                <Phone size={14} /> Call {tracking.driver_phone}
              </a>
              {waLink && (
                <a href={waLink} target="_blank" rel="noopener noreferrer" data-testid="driver-whatsapp-btn"
                  style={{ display: 'inline-flex', alignItems: 'center', gap: '6px', padding: '8px 14px', borderRadius: '8px', background: '#25D366', color: 'white', fontSize: '0.85rem', fontWeight: 600, textDecoration: 'none' }}>
                  <MessageCircle size={14} /> WhatsApp
                </a>
              )}
            </div>
          )}
        </div>
      )}

      {/* Timeline */}
      {!isCancelled && (
        <div data-testid="tracking-timeline" style={{ marginTop: '20px', padding: '20px', background: 'white', border: '1px solid var(--border-gray)', borderRadius: '12px' }}>
          <h3 style={{ margin: '0 0 16px', fontSize: '1rem', color: 'var(--navy)' }}>Order Progress</h3>
          {TIMELINE_STEPS.map((step, idx) => {
            const reached = idx <= currentIndex;
            const isCurrent = idx === currentIndex;
            const Icon = step.icon;
            const time = stepTimes[step.key];
            return (
              <div key={step.key} data-testid={`timeline-step-${step.key}`}
                style={{ display: 'flex', gap: '12px', marginBottom: idx === TIMELINE_STEPS.length - 1 ? 0 : '4px' }}>
                <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                  <div style={{
                    width: '32px', height: '32px', borderRadius: '50%',
                    background: reached ? 'var(--lime)' : 'var(--light-gray)',
                    color: reached ? 'var(--navy)' : 'var(--text-muted)',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    border: isCurrent ? '3px solid var(--navy)' : 'none', flexShrink: 0,
                  }}>
                    <Icon size={16} />
                  </div>
                  {idx < TIMELINE_STEPS.length - 1 && (
                    <div style={{ width: '2px', flex: 1, minHeight: '28px', background: idx < currentIndex ? 'var(--lime)' : 'var(--border-gray)' }} />
                  )}
                </div>
                <div style={{ paddingBottom: '16px', flex: 1 }}>
                  <div style={{ fontWeight: isCurrent ? 700 : 500, color: reached ? 'var(--navy)' : 'var(--text-muted)' }}>
                    {step.label}
                  </div>
                  {time && (
                    <div style={{ fontSize: '0.78rem', color: 'var(--text-muted)', marginTop: '2px' }}>
                      {formatTime(time)}
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}

      <div style={{ marginTop: '16px', textAlign: 'center', fontSize: '0.78rem', color: 'var(--text-muted)' }}>
        This page auto-refreshes every 30 seconds.
      </div>
    </div>
  );
}
